import { reportContextFields, selectedAttachmentIds, intakePrefill } from './report-context';
import { z } from 'zod';
import { TICKET_TYPES } from './enums';

/**
 * Ticket assistant: a read-only coding agent that explores the project repository to verify requests.
 * `draft` sessions are chats that turn a conversation into a ticket; `review` sessions check a submitted ticket.
 */
export const ASSISTANT_SESSION_KINDS = ['draft', 'review'] as const;

/**
 * queued/running: work pending or in progress. idle: the draft waits for the user. failed: the last run
 * failed and can be retried. submitted: the draft became a ticket. completed: the review finished.
 */
export const ASSISTANT_SESSION_STATES = [
  'queued',
  'running',
  'idle',
  'failed',
  'submitted',
  'completed',
] as const;
export type AssistantSessionState = (typeof ASSISTANT_SESSION_STATES)[number];
export const ASSISTANT_BUSY_STATES: readonly AssistantSessionState[] = ['queued', 'running'];

export const ASSISTANT_MESSAGE_ROLES = ['user', 'assistant'] as const;

/** Admin-selected model provider. Any OpenAI-compatible Chat Completions endpoint works. */
export const ASSISTANT_PROVIDERS = ['minimax', 'openai_compatible'] as const;
export type AssistantProvider = (typeof ASSISTANT_PROVIDERS)[number];

export interface AssistantSettings {
  enabled: boolean;
  provider: AssistantProvider;
  baseUrl: string;
  model: string;
  /** Review tickets submitted through the form in the background. */
  reviewOnCreate: boolean;
  /** Tool-use budget per run (searches, file reads). */
  maxSteps: number;
  temperature: number;
  /**
   * The model's context window in tokens. Long explorations and conversations are compacted (old tool output
   * dropped, then summarized) as they approach it.
   */
  contextWindow: number;
}

/** MiniMax M3 through its OpenAI-compatible endpoint is the default model. */
export const DEFAULT_ASSISTANT_SETTINGS: AssistantSettings = {
  enabled: true,
  provider: 'minimax',
  baseUrl: 'https://api.minimax.io/v1',
  model: 'MiniMax-M3',
  reviewOnCreate: true,
  maxSteps: 24,
  temperature: 0.3,
  contextWindow: 1_000_000,
};

const line = (max: number, name: string) =>
  z.string().trim().min(1, `${name} is required`).max(max, `${name} must be at most ${max} characters`);

export const assistantSettingsInput = z.object({
  enabled: z.boolean(),
  provider: z.enum(ASSISTANT_PROVIDERS),
  baseUrl: z
    .string()
    .trim()
    .url('Enter the provider base URL, e.g. https://api.minimax.io/v1')
    .refine((u) => /^https?:\/\//.test(u), 'Use an http or https URL')
    .transform((u) => u.replace(/\/+$/, '')),
  model: line(200, 'Model'),
  reviewOnCreate: z.boolean(),
  maxSteps: z.number().int().min(2).max(60),
  temperature: z.number().min(0).max(2),
  contextWindow: z
    .number()
    .int()
    .min(32_000, 'The assistant needs a context window of at least 32,000 tokens')
    .max(10_000_000),
});

export const assistantMessageInput = z
  .object({
    content: line(8000, 'Message'),
    context: intakePrefill.optional(),
  })
  .transform(({ content, context }) => ({
    content:
      context && Object.values(context).some(Boolean)
        ? `${content}\n\nReport context:\n${JSON.stringify(context)}`
        : content,
  }));

const shortText = (max: number) => z.string().trim().max(max);

/** Ticket fields the assistant proposes; the requester can change any of them before submitting. */
export const ticketDraft = z.object({
  ...reportContextFields,
  type: z.enum(TICKET_TYPES),
  title: line(200, 'Title'),
  description: line(50_000, 'Description'),
  acceptanceCriteria: z.array(shortText(2000)).max(50).default([]),
  stepsOrOutcome: shortText(20_000).default(''),
  labels: z.array(shortText(40).min(1)).max(20).default([]),
});
export type TicketDraft = z.infer<typeof ticketDraft>;

export const codeReference = z.object({
  path: shortText(500).min(1),
  reason: shortText(500).default(''),
});

/** Assistant analysis kept with the draft and shown on the created ticket. */
export const draftAnalysis = z.object({
  summary: shortText(4000).default(''),
  openQuestions: z.array(shortText(1000)).max(20).default([]),
  affectedAreas: z.array(codeReference).max(40).default([]),
  possibleDuplicates: z.array(shortText(40)).max(10).default([]),
});
export type DraftAnalysis = z.infer<typeof draftAnalysis>;

export const ASSISTANT_REVIEW_VERDICTS = ['ready', 'needs_clarification', 'needs_rework'] as const;

/** Background review of a submitted ticket. Suggestions are proposals until a person applies them. */
export const reviewResult = z.object({
  verdict: z.enum(ASSISTANT_REVIEW_VERDICTS),
  /** 1 (unusable) … 5 (implementation-ready). */
  qualityScore: z.number().int().min(1).max(5),
  summary: shortText(4000),
  missingInformation: z.array(shortText(1000)).max(20).default([]),
  questions: z.array(shortText(1000)).max(20).default([]),
  relatedCode: z.array(codeReference).max(40).default([]),
  possibleDuplicates: z.array(shortText(40)).max(10).default([]),
  implementationNotes: shortText(20_000).default(''),
  suggested: z
    .object({
      title: shortText(200).optional(),
      description: shortText(50_000).optional(),
      acceptanceCriteria: z.array(shortText(2000)).max(50).optional(),
      stepsOrOutcome: shortText(20_000).optional(),
    })
    .default({}),
});
export type ReviewResult = z.infer<typeof reviewResult>;

/** Rounds of clarifying questions per draft conversation; after the last one the assistant must propose. */
export const MAX_CLARIFICATION_ROUNDS = 3;

/** One clarifying question. The requester picks from `options` (none means free text) or writes their own answer. */
export const clarifyingQuestion = z.object({
  question: line(1000, 'Question'),
  options: z.array(shortText(200).min(1)).max(5).default([]),
  multiSelect: z.boolean().default(false),
});
export type ClarifyingQuestion = z.infer<typeof clarifyingQuestion>;

/** A round of clarifying questions, kept on the assistant message so the chat can render clickable options. */
export const assistantQuestionPrompt = z.object({
  message: line(8000, 'Message'),
  questions: z.array(clarifyingQuestion).min(1).max(5),
});
export type AssistantQuestionPrompt = z.infer<typeof assistantQuestionPrompt>;

export const submitDraftInput = ticketDraft.extend({ attachmentIds: selectedAttachmentIds });

export const SUGGESTION_FIELDS = ['title', 'description', 'acceptanceCriteria', 'stepsOrOutcome'] as const;
export type SuggestionField = (typeof SUGGESTION_FIELDS)[number];

export const applySuggestionInput = z.object({
  expectedVersion: z.number().int().positive(),
  reviewId: z.string().uuid(),
  fields: z.array(z.enum(SUGGESTION_FIELDS)).min(1, 'Choose at least one suggestion to apply'),
});

/** One step the agent took, shown live while it works. */
export interface AssistantActivity {
  at: string;
  kind: 'tool' | 'note';
  label: string;
  detail?: string;
}
